import { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { Copy, Edit, Info, CheckCircle, AlertCircle, Check, Trash2Icon,  Grid, BarChart2, X } from 'lucide-react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ApplicationFormData, updateApplication, deleteApp } from '../../../api/ApiEntreprise';
import SidebarApp from '../../../components/applications/Roles/Modals/SidebarApp';
import { useTranslation } from '../../../../TranslationContext';
const API_BASE_URL = import.meta.env.VITE_API;

// Fonction fetcher pour SWR
const fetcher = async (url: string) => {
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

export default function SettingApp() {
  const { t } = useTranslation();
  const [copiedItem, setCopiedItem] = useState<string | null>("");
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  
  // États pour la suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // États pour la modal de modification
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<Partial<ApplicationFormData>>({
    name: '',
    displayName: '',
    redirectUri: '',
    postLogoutRedirectUris: ''
  });

  // États pour les notifications
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Récupérer le company ID
  const companyId = localStorage.getItem('company');

  // Charger les données de l'application
  const { data: appData, error, isLoading } = useSWR(
    appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}` : null,
    fetcher
  );

  useEffect(() => {
    mutate(`${API_BASE_URL}/Company/${companyId}/applications/${appId}`);
  }, []);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  // Fonction pour charger les données dans le formulaire
  const loadFormData = () => {
    if (appData?.data) {
      setFormData({
        name: appData.data.name || '',
        displayName: appData.data.displayName || '',
        redirectUri: appData.data.redirectUris || '',
        postLogoutRedirectUris: appData.data.postLogoutRedirectUris || ''
      });
      console.log( 'appdata',appData)
    }
  };

  // Fonction pour copier le texte dans le presse-papier
  const copyToClipboard = (text: string, itemName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(itemName);
      setSuccessMessage(t('copied_to_clipboard'));
      setTimeout(() => {
        setCopiedItem(null);
      }, 2000);
    });
  };
  // console.log('data', appData)
  // Fonction pour afficher le modal de suppression
  function handleDelete(): void {
    setShowDeleteModal(true);
  }

  // Fonction pour confirmer la suppression
  const confirmDelete = async () => {
    try {
      await deleteApp(companyId!, appId!);
      setSuccessMessage(t('application_deleted_successfully'));
      setTimeout(() => {
        navigate('/EntrepriseDashboard');
      }, 1000);
    } catch (error) {
      console.error(t('error_delete_app_log'), error);
      setErrorMessage(t('failed_to_delete_application'));
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Mettre à jour le formulaire quand on clique sur modifier
  const handleEditClick = () => {
    loadFormData();
    setShowEditModal(true);
  };

  // Gérer les changements dans le formulaire
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Soumettre le formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateApplication(companyId!, appId!, formData);
      setShowEditModal(false);
      mutate(`${API_BASE_URL}/Company/${companyId}/applications/${appId}`);
      setSuccessMessage(t('application_updated_successfully'));
    } catch (error) {
      console.error(t('error_update_app_log'), error);
      setErrorMessage(t('failed_to_update_application'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-6">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-slate-200 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full p-6">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            <p>{t('error_loading')}</p>
          </div>
        </div>
      </div>
    );
  }
  console.log('data', appData)
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Messages de notification */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md animate-fade-in-out max-w-md z-50 flex items-center">
          <div className="mr-3 flex-shrink-0">
            <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <p>{successMessage}</p>
        </div>
      )}
      
      {errorMessage && (
        <div className="fixed top-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md animate-fade-in-out max-w-md z-50 flex items-center">
          <div className="mr-3 flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <p>{errorMessage}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row">
        <SidebarApp />
        <div className="w-full space-y-6">
          {/* En-tête */}
          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h1 className="text-2xl font-semibold text-gray-800 flex items-center">
                <Grid className="mr-2 h-6 w-6 text-gray-600" />
                {appData?.data.displayName} - {t('settings')}
              </h1>
              <div className="flex gap-3">
                <button
                  onClick={handleEditClick}
                  className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-lg transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 shadow-sm"
                  aria-label={t('update_settings')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t('update_settings')}
                </button>
              </div>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Section Nom de l'application */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 rounded-lg bg-red-100 p-3">
                    <Grid className="h-6 w-6 text-red-500" />
                  </div>
                  <h2 className="ml-3 text-lg font-medium text-gray-800">{t('application_name')}</h2>
                  <button
                    onClick={() => copyToClipboard(appData?.data.displayName, 'displayName')}
                    className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
                    title={t('copy')}
                    aria-label={t('copy_application_name')}
                  >
                    {copiedItem === 'displayName' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100">
                  {appData?.data.displayName}
                </div>
              </div>

              {/* Section Client ID */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 rounded-lg bg-yellow-100 p-3">
                    <Info className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h2 className="ml-3 text-lg font-medium text-gray-800">{t('client_id')}</h2>
                  <button
                    onClick={() => copyToClipboard(appData?.data.clientId, 'clientId')}
                    className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
                    title={t('copy')}
                    aria-label={t('copy_client_id')}
                  >
                    {copiedItem === 'clientId' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 overflow-x-auto">
                  {appData?.data.clientId}
                </div>
              </div>

              {/* Section Redirect URI */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 rounded-lg bg-blue-100 p-3">
                    <BarChart2 className="h-6 w-6 text-blue-500" />
                  </div>
                  <h2 className="ml-3 text-lg font-medium text-gray-800">{t('redirect_uri')}</h2>
                  <button
                    onClick={() => copyToClipboard(appData?.data.redirectUris, 'redirectUris')}
                    className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
                    title={t('copy')}
                    aria-label={t('copy_redirect_uri')}
                  >
                    {copiedItem === 'redirectUris' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 overflow-x-auto">
                  {appData?.data.redirectUris}
                </div>
              </div>

              {/* Section Post-Logout Redirect URIs */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 rounded-lg bg-purple-100 p-3">
                    <BarChart2 className="h-6 w-6 text-purple-500" />
                  </div>
                  <h2 className="ml-3 text-lg font-medium text-gray-800">{t('post_logout_redirect_uris')}</h2>
                  <button
                    onClick={() => copyToClipboard(appData?.data.postLogoutRedirectUris, 'postLogoutRedirectUris')}
                    className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
                    title={t('copy')}
                    aria-label={t('copy_post_logout_redirect_uris')}
                  >
                    {copiedItem === 'postLogoutRedirectUris' ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 overflow-x-auto">
                  {appData?.data.postLogoutRedirectUris ? appData.data.postLogoutRedirectUris.replace(/[\[\]"\\]/g, '') : t('no_post_logout_redirect_uris')}
                </div>
              </div>

              {/* Section Description */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 rounded-lg bg-indigo-100 p-3">
                    <Info className="h-6 w-6 text-indigo-500" />
                  </div>
                  <h2 className="ml-3 text-lg font-medium text-gray-800">{t('description')}</h2>
                </div>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 min-h-20">
                  {appData?.data.description || t('no_description')}
                </div>
              </div>

              {/* Section Statut */}
              <div className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 rounded-lg bg-green-100 p-3">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  </div>
                  <h2 className="ml-3 text-lg font-medium text-gray-800">{t('status')}</h2>
                </div>
                <div className="flex items-center">
                  <span
                    className={`px-4 py-2 inline-flex text-sm leading-5 font-semibold rounded-full 
                      ${appData?.data.status === 'True' || appData?.data.status === true ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                  >
                    {appData?.data.status === 'True' || appData?.data.status === true ? (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <AlertCircle className="h-4 w-4 mr-2" />
                    )}
                    {appData?.data.status === 'True' || appData?.data.status === true ? t('active') : t('blocked')}
                  </span>
                </div>
              </div>
            </div>

            {/* Bouton Supprimer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleDelete}
                className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 flex items-center transition-colors"
                aria-label={t('delete_app')}
              >
                <Trash2Icon className="h-4 w-4 mr-2" />
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl transform transition-all animate-scale-in">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
              {t('confirm_deletion')}
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              {t('delete_confirmation', { appName: appData?.data.displayName })}

            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                aria-label={t('cancel')}
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                aria-label={t('delete')}
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {showEditModal && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl transform transition-all animate-scale-in">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                <Edit className="mr-2 h-5 w-5 text-gray-600" />
                {t('edit_application')}
              </h3>
              <button 
                onClick={() => setShowEditModal(false)} 
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Nom de l'application */}
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                    {t('application_name')}
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                    required
                    aria-required="true"
                  />
                </div>
                
                {/* Client ID */}
                <div>
                  <label htmlFor="clientId" className="block text-sm font-medium text-gray-700">
                    {t('client_id')}
                  </label>
                  <input
                    type="text"
                    id="clientId"
                    name="clientId"
                    value={appData?.data.clientId || ''}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 bg-gray-100"
                    readOnly
                    aria-readonly="true"
                  />
                </div>
                
                {/* Redirect URI */}
                <div>
                  <label htmlFor="redirectUri" className="block text-sm font-medium text-gray-700">
                    {t('redirect_uri')}
                  </label>
                  <input
                    type="text"
                    id="redirectUri"
                    name="redirectUri"
                    value={formData.redirectUri || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                    required
                    aria-required="true"
                  />
                </div>
                
                {/* Post-Logout Redirect URI */}
                <div>
                  <label htmlFor="postLogoutRedirectUris" className="block text-sm font-medium text-gray-700">
                    {t('post_logout_redirect_uris')}
                  </label>
                  <input
                    type="text"
                    id="postLogoutRedirectUris"
                    name="postLogoutRedirectUris"
                    value={formData.postLogoutRedirectUris || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                    aria-required="false"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  aria-label={t('cancel')}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 shadow-sm"
                  aria-label={t('save')}
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
// import { useEffect, useState } from 'react';
// import useSWR, { mutate } from 'swr';
// import { Copy, Edit, Info, CheckCircle, AlertCircle, Check, Trash2Icon,  Grid, BarChart2, X } from 'lucide-react';
// import axios from 'axios';
// import { useParams, useNavigate } from 'react-router-dom';
// import { ApplicationFormData, updateApplication, deleteApp } from './ApiEntreprise';
// import SidebarApp from '../../../components/applications/Roles/Modals/SidebarApp';
// import { useTranslation } from '../../../../TranslationContext';
// const API_BASE_URL = import.meta.env.VITE_API;

// // Fonction fetcher pour SWR
// const fetcher = async (url: string) => {
//   const response = await axios.get(url, {
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem('access_token')}`,
//     },
//   });
//   return response.data;
// };

// export default function SettingApp() {
//   const { t } = useTranslation();
//   const [copiedItem, setCopiedItem] = useState<string | null>("");
//   const { appId } = useParams<{ appId: string }>();
//   const navigate = useNavigate();
  
//   // États pour la suppression
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
  
//   // États pour la modal de modification
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [formData, setFormData] = useState<Partial<ApplicationFormData>>({
//     name: '',
//     displayName: '',
//     redirectUri: ''
//   });

//   // États pour les notifications
//   const [successMessage, setSuccessMessage] = useState<string | null>(null);
//   const [errorMessage, setErrorMessage] = useState<string | null>(null);

//   // Récupérer le company ID
//   const companyId = localStorage.getItem('company');

//   // Charger les données de l'application
//   const { data: appData, error, isLoading } = useSWR(
//     appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}` : null,
//     fetcher
//   );

//   useEffect(() => {
//     mutate(`${API_BASE_URL}/Company/${companyId}/applications/${appId}`);
//   }, []);

//   useEffect(() => {
//     if (successMessage || errorMessage) {
//       const timer = setTimeout(() => {
//         setSuccessMessage(null);
//         setErrorMessage(null);
//       }, 3000);
//       return () => clearTimeout(timer);
//     }
//   }, [successMessage, errorMessage]);

//   // Fonction pour charger les données dans le formulaire
//   const loadFormData = () => {
//     if (appData?.data) {
//       setFormData({
//         name: appData.data.name || '',
//         displayName: appData.data.displayName || '',
//         redirectUri: appData.data.redirectUris || ''
//       });
//     }
//   };

//   // Fonction pour copier le texte dans le presse-papier
//   const copyToClipboard = (text: string, itemName: string) => {
//     navigator.clipboard.writeText(text).then(() => {
//       setCopiedItem(itemName);
//       setSuccessMessage(t('copied_to_clipboard'));
//       setTimeout(() => {
//         setCopiedItem(null);
//       }, 2000);
//     });
//   };
//   // console.log('data', appData)
//   // Fonction pour afficher le modal de suppression
//   function handleDelete(): void {
//     setShowDeleteModal(true);
//   }

//   // Fonction pour confirmer la suppression
//   const confirmDelete = async () => {
//     try {
//       await deleteApp(companyId!, appId!);
//       setSuccessMessage(t('application_deleted_successfully'));
//       setTimeout(() => {
//         navigate('/EntrepriseDashboard');
//       }, 1000);
//     } catch (error) {
//       console.error(t('error_delete_app_log'), error);
//       setErrorMessage(t('failed_to_delete_application'));
//     } finally {
//       setShowDeleteModal(false);
//     }
//   };

//   // Mettre à jour le formulaire quand on clique sur modifier
//   const handleEditClick = () => {
//     loadFormData();
//     setShowEditModal(true);
//   };

//   // Gérer les changements dans le formulaire
//   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   // Soumettre le formulaire
//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     try {
//       await updateApplication(companyId!, appId!, formData);
//       setShowEditModal(false);
//       mutate(`${API_BASE_URL}/Company/${companyId}/applications/${appId}`);
//       setSuccessMessage(t('application_updated_successfully'));
//     } catch (error) {
//       console.error(t('error_update_app_log'), error);
//       setErrorMessage(t('failed_to_update_application'));
//     }
//   };

//   if (isLoading) {
//     return (
//       <div className="flex justify-center items-center h-full p-6">
//         <div className="animate-pulse flex space-x-4">
//           <div className="rounded-full bg-slate-200 h-12 w-12"></div>
//           <div className="flex-1 space-y-4 py-1">
//             <div className="h-4 bg-slate-200 rounded w-3/4"></div>
//             <div className="space-y-2">
//               <div className="h-4 bg-slate-200 rounded"></div>
//               <div className="h-4 bg-slate-200 rounded w-5/6"></div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex justify-center items-center h-full p-6">
//         <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
//           <div className="flex items-center">
//             <AlertCircle className="h-5 w-5 mr-2" />
//             <p>{t('error_loading')}</p>
//           </div>
//         </div>
//       </div>
//     );
//   }
//   console.log('data', appData)
//   return (
//     <div className="p-6 bg-gray-50 min-h-screen">
//       {/* Messages de notification */}
//       {successMessage && (
//         <div className="fixed top-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md animate-fade-in-out max-w-md z-50 flex items-center">
//           <div className="mr-3 flex-shrink-0">
//             <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//             </svg>
//           </div>
//           <p>{successMessage}</p>
//         </div>
//       )}
      
//       {errorMessage && (
//         <div className="fixed top-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md animate-fade-in-out max-w-md z-50 flex items-center">
//           <div className="mr-3 flex-shrink-0">
//             <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
//             </svg>
//           </div>
//           <p>{errorMessage}</p>
//         </div>
//       )}

//       <div className="flex flex-col md:flex-row">
//         <SidebarApp />
//         <div className="w-full space-y-6">
//           {/* En-tête */}
//           <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
//             <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//               <h1 className="text-2xl font-semibold text-gray-800 flex items-center">
//                 <Grid className="mr-2 h-6 w-6 text-gray-600" />
//                 {appData?.data.displayName} - {t('settings')}
//               </h1>
//               <div className="flex gap-3">
//                 <button
//                   onClick={handleEditClick}
//                   className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-lg transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 shadow-sm"
//                   aria-label={t('update_settings')}
//                 >
//                   <Edit className="h-4 w-4 mr-2" />
//                   {t('update_settings')}
//                 </button>
//               </div>
//             </div>
//           </div>

//           {/* Contenu principal */}
//           <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               {/* Section Nom de l'application */}
//               <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
//                 <div className="flex items-center mb-4">
//                   <div className="flex-shrink-0 rounded-lg bg-red-100 p-3">
//                     <Grid className="h-6 w-6 text-red-500" />
//                   </div>
//                   <h2 className="ml-3 text-lg font-medium text-gray-800">{t('application_name')}</h2>
//                   <button
//                     onClick={() => copyToClipboard(appData?.data.displayName, 'displayName')}
//                     className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
//                     title={t('copy')}
//                     aria-label={t('copy_application_name')}
//                   >
//                     {copiedItem === 'displayName' ? (
//                       <Check className="h-4 w-4 text-green-600" />
//                     ) : (
//                       <Copy className="h-4 w-4" />
//                     )}
//                   </button>
//                 </div>
//                 <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100">
//                   {appData?.data.displayName}
//                 </div>
//               </div>

//               {/* Section Client ID */}
//               <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
//                 <div className="flex items-center mb-4">
//                   <div className="flex-shrink-0 rounded-lg bg-yellow-100 p-3">
//                     <Info className="h-6 w-6 text-yellow-600" />
//                   </div>
//                   <h2 className="ml-3 text-lg font-medium text-gray-800">{t('client_id')}</h2>
//                   <button
//                     onClick={() => copyToClipboard(appData?.data.clientId, 'clientId')}
//                     className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
//                     title={t('copy')}
//                     aria-label={t('copy_client_id')}
//                   >
//                     {copiedItem === 'clientId' ? (
//                       <Check className="h-4 w-4 text-green-600" />
//                     ) : (
//                       <Copy className="h-4 w-4" />
//                     )}
//                   </button>
//                 </div>
//                 <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 overflow-x-auto">
//                   {appData?.data.clientId}
//                 </div>
//               </div>

//               {/* Section Redirect URI */}
//               <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
//                 <div className="flex items-center mb-4">
//                   <div className="flex-shrink-0 rounded-lg bg-blue-100 p-3">
//                     <BarChart2 className="h-6 w-6 text-blue-500" />
//                   </div>
//                   <h2 className="ml-3 text-lg font-medium text-gray-800">{t('redirect_uri')}</h2>
//                   <button
//                     onClick={() => copyToClipboard(appData?.data.redirectUris, 'redirectUris')}
//                     className="ml-auto text-slate-600 hover:text-slate-900 p-1 rounded-full hover:bg-gray-100"
//                     title={t('copy')}
//                     aria-label={t('copy_redirect_uri')}
//                   >
//                     {copiedItem === 'redirectUris' ? (
//                       <Check className="h-4 w-4 text-green-600" />
//                     ) : (
//                       <Copy className="h-4 w-4" />
//                     )}
//                   </button>
//                 </div>
//                 <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 overflow-x-auto">
//                   {appData?.data.redirectUris}
//                 </div>
//               </div>

//               {/* Section Description */}
//               <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
//                 <div className="flex items-center mb-4">
//                   <div className="flex-shrink-0 rounded-lg bg-indigo-100 p-3">
//                     <Info className="h-6 w-6 text-indigo-500" />
//                   </div>
//                   <h2 className="ml-3 text-lg font-medium text-gray-800">{t('description')}</h2>
//                 </div>
//                 <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-md border border-gray-100 min-h-20">
//                   {appData?.data.description || t('no_description')}
//                 </div>
//               </div>

//               {/* Section Statut */}
//               <div className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
//                 <div className="flex items-center mb-4">
//                   <div className="flex-shrink-0 rounded-lg bg-green-100 p-3">
//                     <CheckCircle className="h-6 w-6 text-green-500" />
//                   </div>
//                   <h2 className="ml-3 text-lg font-medium text-gray-800">{t('status')}</h2>
//                 </div>
//                 <div className="flex items-center">
//                   <span
//                     className={`px-4 py-2 inline-flex text-sm leading-5 font-semibold rounded-full 
//                       ${appData?.data.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
//                   >
//                     {appData?.data.status === 'active' ? (
//                       <CheckCircle className="h-4 w-4 mr-2" />
//                     ) : (
//                       <AlertCircle className="h-4 w-4 mr-2" />
//                     )}
//                     {t(`status_${appData?.data.status}`)}
//                   </span>
//                 </div>
//               </div>
//             </div>

//             {/* Bouton Supprimer */}
//             <div className="mt-6 flex justify-end">
//               <button
//                 onClick={handleDelete}
//                 className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 flex items-center transition-colors"
//                 aria-label={t('delete_app')}
//               >
//                 <Trash2Icon className="h-4 w-4 mr-2" />
//                 {t('delete')}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Modal de confirmation de suppression */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4 animate-fade-in">
//           <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl transform transition-all animate-scale-in">
//             <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
//               <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
//               {t('confirm_deletion')}
//             </h3>
//             <p className="text-sm text-gray-700 mb-6">
//               {t('delete_confirmation', { appName: appData?.data.displayName })}

//             </p>
//             <div className="flex justify-end space-x-3">
//               <button
//                 onClick={() => setShowDeleteModal(false)}
//                 className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
//                 aria-label={t('cancel')}
//               >
//                 {t('cancel')}
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
//                 aria-label={t('delete')}
//               >
//                 {t('delete')}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Modal de modification */}
//       {showEditModal && (
//         <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4 animate-fade-in">
//           <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl transform transition-all animate-scale-in">
//             <div className="flex justify-between items-center mb-6">
//               <h3 className="text-xl font-semibold text-gray-800 flex items-center">
//                 <Edit className="mr-2 h-5 w-5 text-gray-600" />
//                 {t('edit_application')}
//               </h3>
//               <button 
//                 onClick={() => setShowEditModal(false)} 
//                 className="text-gray-500 hover:text-gray-700 focus:outline-none"
//               >
//                 <X className="h-5 w-5" />
//               </button>
//             </div>
            
//             <form onSubmit={handleSubmit}>
//               <div className="space-y-4">
//                 {/* Nom de l'application */}
//                 <div>
//                   <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
//                     {t('application_name')}
//                   </label>
//                   <input
//                     type="text"
//                     id="displayName"
//                     name="displayName"
//                     value={formData.displayName || ''}
//                     onChange={handleChange}
//                     className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
//                     required
//                     aria-required="true"
//                   />
//                 </div>
                
//                 {/* Client ID */}
//                 <div>
//                   <label htmlFor="clientId" className="block text-sm font-medium text-gray-700">
//                     {t('client_id')}
//                   </label>
//                   <input
//                     type="text"
//                     id="clientId"
//                     name="clientId"
//                     value={appData?.data.clientId || ''}
//                     className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 bg-gray-100"
//                     readOnly
//                     aria-readonly="true"
//                   />
//                 </div>
                
//                 {/* Redirect URI */}
//                 <div>
//                   <label htmlFor="redirectUri" className="block text-sm font-medium text-gray-700">
//                     {t('redirect_uri')}
//                   </label>
//                   <input
//                     type="text"
//                     id="redirectUri"
//                     name="redirectUri"
//                     value={formData.redirectUri || ''}
//                     onChange={handleChange}
//                     className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
//                     required
//                     aria-required="true"
//                   />
//                 </div>
//               </div>
              
//               <div className="flex justify-end space-x-3 mt-6">
//                 <button
//                   type="button"
//                   onClick={() => setShowEditModal(false)}
//                   className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
//                   aria-label={t('cancel')}
//                 >
//                   {t('cancel')}
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 shadow-sm"
//                   aria-label={t('save')}
//                 >
//                   {t('save')}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }